'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetTradesRow, hashDiagnosticEntity, useGetTrades } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef } from 'react';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { MARKET_TRADES_TAPE_FIELDS } from '@/lib/sdk/projections';
import { logger } from '@/lib/telemetry/logger';
interface UseWinnerLiveTradesInput {
  readonly marketPubkey: string;
  readonly outcomeMint: string;
  readonly originalOutcomeIndex: number;
  readonly tradesFrom: string;
  readonly chartHistoryCutoffMs?: number;
  readonly liveChartEnabled: boolean;
}
export function useWinnerLiveTrades(t0) {
  const $ = _c(49);
  const {
    marketPubkey,
    outcomeMint,
    originalOutcomeIndex,
    tradesFrom,
    chartHistoryCutoffMs,
    liveChartEnabled
  } = t0;
  let t1;
  if ($[0] !== chartHistoryCutoffMs) {
    t1 = chartHistoryCutoffMs === undefined ? undefined : new Date(chartHistoryCutoffMs).toISOString();
    $[0] = chartHistoryCutoffMs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const cutoffIso = t1;
  const queryEnabled = cutoffIso !== undefined;
  const t2 = String(originalOutcomeIndex);
  let t3;
  if ($[2] !== outcomeMint) {
    t3 = [outcomeMint];
    $[2] = outcomeMint;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== marketPubkey || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      market: marketPubkey,
      outcomeIndex: t2,
      mints: t3,
      sortBy: "timestamp",
      sortOrder: "desc"
    };
    $[4] = marketPubkey;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const sharedScope = t4 as const;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [...MARKET_TRADES_TAPE_FIELDS];
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== cutoffIso || $[10] !== sharedScope) {
    t6 = {
      ...sharedScope,
      from: cutoffIso,
      fields: t5
    };
    $[9] = cutoffIso;
    $[10] = sharedScope;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = queryEnabled && liveChartEnabled;
  let t8;
  if ($[12] !== liveChartEnabled || $[13] !== queryEnabled) {
    t8 = queryEnabled && liveChartEnabled ? {
      eagerSnapshot: false,
      fetchOnce: true
    } : {};
    $[12] = liveChartEnabled;
    $[13] = queryEnabled;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = {
      enabled: t7,
      ...t8
    };
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const liveQuery = useGetTrades(t6, t9);
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [...MARKET_TRADES_TAPE_FIELDS];
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== cutoffIso || $[20] !== sharedScope || $[21] !== tradesFrom) {
    t11 = {
      ...sharedScope,
      from: tradesFrom,
      to: cutoffIso,
      fields: t10
    };
    $[19] = cutoffIso;
    $[20] = sharedScope;
    $[21] = tradesFrom;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== queryEnabled) {
    t12 = queryEnabled ? {
      eagerSnapshot: false,
      fetchOnce: true
    } : {};
    $[23] = queryEnabled;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== queryEnabled || $[26] !== t12) {
    t13 = {
      enabled: queryEnabled,
      ...t12
    };
    $[25] = queryEnabled;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const anchorQuery = useGetTrades(t11, t13);
  const liveRows = useLiveListData(liveQuery.queryKey, liveQuery.rows, liveQuery.entityQueryKey, liveQuery.projectEntityRow, liveChartEnabled);
  let t14;
  if ($[28] !== marketPubkey) {
    t14 = hashDiagnosticEntity(marketPubkey);
    $[28] = marketPubkey;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const marketHash = t14;
  let t15;
  if ($[30] !== outcomeMint) {
    t15 = hashDiagnosticEntity(outcomeMint);
    $[30] = outcomeMint;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const mintHash = t15;
  useWinnerTradePagination(liveQuery, queryEnabled && liveChartEnabled && liveQuery.hasNextPage, marketHash, mintHash);
  const t16 = liveChartEnabled && liveQuery.isError;
  let t17;
  if ($[32] !== liveQuery.error || $[33] !== marketHash || $[34] !== mintHash || $[35] !== t16) {
    t17 = {
      isError: t16,
      error: liveQuery.error,
      stage: "live_query",
      marketHash,
      mintHash
    };
    $[32] = liveQuery.error;
    $[33] = marketHash;
    $[34] = mintHash;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  useWinnerTradeQueryWarning(t17);
  const t18 = queryEnabled && anchorQuery.isError;
  let t19;
  if ($[37] !== anchorQuery.error || $[38] !== marketHash || $[39] !== mintHash || $[40] !== t18) {
    t19 = {
      isError: t18,
      error: anchorQuery.error,
      stage: "anchor_query",
      marketHash,
      mintHash
    };
    $[37] = anchorQuery.error;
    $[38] = marketHash;
    $[39] = mintHash;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  useWinnerTradeQueryWarning(t19);
  const anchor = anchorQuery.rows[0];
  if (anchor === undefined) {
    let t20;
    if ($[42] !== liveChartEnabled || $[43] !== liveRows) {
      t20 = liveChartEnabled ? liveRows : [];
      $[42] = liveChartEnabled;
      $[43] = liveRows;
      $[44] = t20;
    } else {
      t20 = $[44];
    }
    return t20;
  }
  let t20;
  if ($[45] !== anchor || $[46] !== liveChartEnabled || $[47] !== liveRows) {
    t20 = liveChartEnabled ? [anchor, ...liveRows] : [anchor];
    $[45] = anchor;
    $[46] = liveChartEnabled;
    $[47] = liveRows;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  return t20;
}
interface PaginatedTradeQuery {
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly fetchNextPage: () => Promise<void>;
}
function useWinnerTradePagination(query, enabled, marketHash, mintHash) {
  const $ = _c(8);
  const warnedErrorClass = useRef(null);
  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = query;
  let t0;
  let t1;
  if ($[0] !== enabled || $[1] !== fetchNextPage || $[2] !== hasNextPage || $[3] !== isFetchingNextPage || $[4] !== marketHash || $[5] !== mintHash) {
    t0 = () => {
      if (!enabled || !hasNextPage || isFetchingNextPage) {
        return;
      }
      fetchNextPage().catch(error => {
        const errorClass = error instanceof Error ? error.name : "unknown";
        if (warnedErrorClass.current === errorClass) {
          return;
        }
        warnedErrorClass.current = errorClass;
        logger.warn("winner_detail.live_chart_trades_unavailable", {
          stage: "pagination",
          retryable: true,
          errorClass,
          marketHash,
          mintHash
        });
      });
    };
    t1 = [enabled, fetchNextPage, hasNextPage, isFetchingNextPage, marketHash, mintHash];
    $[0] = enabled;
    $[1] = fetchNextPage;
    $[2] = hasNextPage;
    $[3] = isFetchingNextPage;
    $[4] = marketHash;
    $[5] = mintHash;
    $[6] = t0;
    $[7] = t1;
  } else {
    t0 = $[6];
    t1 = $[7];
  }
  useEffect(t0, t1);
}
interface WinnerTradeQueryWarningInput {
  readonly isError: boolean;
  readonly error: Error | null;
  readonly stage: 'live_query' | 'anchor_query';
  readonly marketHash: string;
  readonly mintHash: string;
}
function useWinnerTradeQueryWarning(t0) {
  const $ = _c(6);
  const {
    isError,
    error,
    stage,
    marketHash,
    mintHash
  } = t0;
  const warnedQueryError = useRef(null);
  const errorClass = isError ? error?.name ?? "unknown" : null;
  let t1;
  let t2;
  if ($[0] !== errorClass || $[1] !== marketHash || $[2] !== mintHash || $[3] !== stage) {
    t1 = () => {
      if (errorClass === null) {
        warnedQueryError.current = null;
        return;
      }
      if (warnedQueryError.current === errorClass) {
        return;
      }
      warnedQueryError.current = errorClass;
      logger.warn("winner_detail.live_chart_trades_unavailable", {
        stage,
        retryable: true,
        errorClass,
        marketHash,
        mintHash
      });
    };
    t2 = [errorClass, marketHash, mintHash, stage];
    $[0] = errorClass;
    $[1] = marketHash;
    $[2] = mintHash;
    $[3] = stage;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
}